import {
  CollectionPreferencesProps,
  TableProps,
} from "@amzn/awsui-components-react";
import { UseServerSideDataParams } from "./hooks";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";

// Define the structure for additional filter options
export interface FilterOption {
  label: string;
  field: string;
  selectedOption: OptionDefinition;
  options: OptionDefinition[];
}

// TODO: delete this once migrate to the new component
export interface IServerSideTableProps {
  columnDefinitions: TableProps.ColumnDefinition<any>[];
  saveWidths: TableProps["onColumnWidthsChange"];
  // Add hook function as prop
  queryHook: (params: any) => {
    data?: any;
    isLoading: boolean;
    error?: any;
  };
  transformParams: (params: UseServerSideDataParams) => any;
  transformResponse?: (response: any) => {
    items: any[];
    totalCount: number;
  };
  tableTitle: string;
  preferences: CollectionPreferencesProps.Preferences;
  setPreferences: (preferences: CollectionPreferencesProps.Preferences) => void;
  contentDisplayPreferenceOptions: CollectionPreferencesProps.ContentDisplayOption[];

  selectionType?: TableProps.SelectionType | undefined; // this is to define how to allow user to select rows. If not defined, then user is not allowed to make select rows
  defaultPageSize?: number;
  defaultSortingColumnIdx?: number;
  loadingText?: string;
  pageSizeOptions?: CollectionPreferencesProps.PageSizeOption[]; // default page size options are provided in PAGE_SIZE_OPTIONS
  additionalFilters?: FilterOption[]; // additional filters to be put on the right side of text search box.
  onFilterChange?: (filters: Record<string, string>) => void; // this will notify the parent components about the selected values, this is especially used when some extra dropdowns are defined
  showSearchBar?: boolean; //if search bar is not needed, set it to false. The default value is true
  skipQuery?: boolean; // Skip the backend query when true
}

// Field type detection for smart unique values loading
export type FieldType =
  | "date"
  | "numeric"
  | "boolean"
  | "enum"
  | "text"
  | "freetext";

export interface UniqueValuesConfig {
  enabled: boolean;
  skipFieldTypes: FieldType[];
  loadOnlyVisibleColumns: boolean;
}

// Extended PropertyFilter filtering property with explicit field type
export interface ExtendedFilteringProperty
  extends PropertyFilterProps.FilteringProperty {
  /**
   * Explicit field type for smart unique values loading.
   *
   * Field types and their meanings:
   * - "text": Free-form text fields (names, descriptions) - loads unique values for autocomplete
   * - "freetext": Free-form text fields that should NOT load unique values (very large datasets)
   * - "enum": Predefined set of values (status, priority) - loads unique values for autocomplete
   * - "numeric": Numeric fields (counts, scores) - skips unique values loading by default
   * - "date": Date/time fields - skips unique values loading (uses DatePicker)
   * - "boolean": True/false fields - skips unique values loading
   *
   * If not specified, defaults to "text" and will load unique values.
   */
  fieldType?: FieldType;
}

export interface LoadUniqueValuesFunction {
  (propertyKeys: string[]): Promise<Record<string, string[]>>;
}

/**
 * Data fetching and loading configuration (REQUIRED)
 * These props define how data is fetched and transformed from your backend API.
 */
export interface DataSourceConfig {
  /**
   * React hook that fetches data from your backend API
   * @example useMockTaskSearchQuery, useTasksQuery, etc.
   */
  queryHook: (
    params: any,
    options?: { skip?: boolean; refetchOnMountOrArgChange?: boolean }
  ) => { data?: any; isLoading: boolean; error?: any; refetch?: () => void };

  /**
   * Function to transform component state into API parameters
   * @example (params) => ({ teamName: 'myTeam', page: params.pagination?.currentPageIndex })
   */
  transformParams: (params: {
    pagination?: {
      currentPageIndex?: number;
      pageSize?: number;
      searchAfter?: string[];
    };
    sorting?: {
      sortingColumn?: { sortingField?: string };
      sortingDescending?: boolean;
    };
    propertyFiltering?: {
      filteringQuery?: PropertyFilterProps.Query;
    };
  }) => any;

  /**
   * Function to transform API response into table format
   * @example (response) => ({ items: response.tasks.taskList, totalCount: response.totalCount })
   */
  transformResponse: (response: any) => {
    items: any[];
    totalCount: number;
    filteringOptions?: PropertyFilterProps.FilteringOption[];
    paginationMetadata?: {
      searchAfter?: string[];
      [key: string]: any;
    };
  };

  /**
   * Skip the backend query (useful for conditional loading)
   * @default false
   * @example skipQuery={!teamName} // Don't query until team is selected
   */
  skipQuery?: boolean;
}

/**
 * Table preferences and user settings (REQUIRED)
 * These props handle column management, user preferences, and table behavior settings.
 */
export interface TablePreferencesConfig {
  /**
   * Callback when user resizes columns - save widths to localStorage/state
   * @example (widths) => saveToLocalStorage('table-widths', widths)
   */
  saveWidths: (widths: TableProps.ColumnWidthsChangeDetail) => void;

  /**
   * Current user preferences (page size, column visibility, etc.)
   * Usually loaded from localStorage or user settings
   */
  preferences: any;

  /**
   * Callback when user changes preferences - save to localStorage/state
   * @example (prefs) => setLocalStorage('table-prefs', prefs)
   */
  setPreferences: (preferences: any) => void;

  /**
   * Available columns for show/hide in preferences dialog
   * @example [{ id: 'name', label: 'Task Name', visible: true }]
   */
  contentDisplayPreferenceOptions: any[];

  /**
   * Enable row selection (single, multi, or none)
   * @default undefined (no selection)
   * @example "single", "multi"
   */
  selectionType?: TableProps.SelectionType;

  /**
   * Default number of items per page
   * @default 25
   */
  defaultPageSize?: number;

  /**
   * Index of column to sort by initially (0-based)
   * @default 0
   */
  defaultSortingColumnIdx?: number;

  /**
   * Text shown while data is loading
   * @default "Loading data..."
   */
  loadingText?: string;

  /**
   * Available page size options in preferences
   * @default [10, 25, 50, 100]
   */
  pageSizeOptions?: any[];
}

/**
 * PropertyFilter search and filtering configuration (OPTIONAL)
 * These props control the advanced search and filtering capabilities.
 */
export interface PropertyFilterConfig {
  /**
   * Show the PropertyFilter component above the table
   * @default true
   */
  showPropertyFilter?: boolean;

  /**
   * Field definitions for structured search (with explicit field types)
   * @example [{ propertyLabel: 'Status', key: 'status', fieldType: 'enum' }]
   */
  filteringProperties?: ExtendedFilteringProperty[];

  /**
   * Custom text labels for PropertyFilter UI
   * @example { filteringPlaceholder: 'Search tasks...', tokenEditorAddNewTokenLabel: 'Add filter' }
   */
  propertyFilterI18nStrings?: PropertyFilterProps.I18nStrings;

  /**
   * Enable free-text search across all fields
   * @default true
   */
  enableFreeTextSearch?: boolean;

  /**
   * Placeholder text for free-text search input
   * @default "Search across all fields..."
   */
  freeTextSearchPlaceholder?: string;

  /**
   * Enable structured token-based search (field:value)
   * @default true
   */
  enableTokenSearch?: boolean;
}

/**
 * Smart autocomplete and unique values loading (OPTIONAL)
 * These props enable automatic dropdown population for PropertyFilter fields.
 */
export interface AutocompleteConfig {
  /**
   * RTK Query hook for loading unique values
   * The component will call this hook with { teamName, fields, limit, propertyFilterQuery }
   * @example useGetBatchUniqueValuesQuery
   */
  uniqueValuesQueryHook?: (params: {
    teamName: string;
    fields: string[];
    limit?: number;
    propertyFilterQuery?: any;
  }) => {
    data?: { uniqueValues: Record<string, string[]> };
    isLoading: boolean;
    error?: any;
  };

  /**
   * Parameters for the unique values API call
   * Include parentId filter in propertyFilterQuery for directory scoping
   * @example { teamName: 'my-team', limit: 50, propertyFilterQuery: { tokens: [{ propertyKey: 'parentId', operator: '=', value: 'path' }] } }
   */
  uniqueValuesParams?: {
    teamName: string;
    limit?: number;
    propertyFilterQuery?: any;
    [key: string]: any; // Allow additional params for specific implementations
  };

  /**
   * Configuration for smart unique values loading behavior
   * @example { enabled: true, skipFieldTypes: ['date', 'numeric'], loadOnlyVisibleColumns: true }
   */
  uniqueValuesConfig?: Partial<UniqueValuesConfig>;
}

/**
 * Complete props interface for ServerSideTableWithPropertyFilter
 *
 * USAGE EXAMPLE:
 * ```tsx
 * <ServerSideTableWithPropertyFilter
 *   // Top-level props (most commonly used)
 *   columnDefinitions={taskColumns}
 *   tableTitle="My Tasks"
 *
 *   // Grouped configurations
 *   dataSource={{
 *     queryHook: useTasksQuery,
 *     transformParams: (params) => ({ teamName: 'myTeam', ...params }),
 *     transformResponse: (data) => ({ items: data.tasks, totalCount: data.total }),
 *     skipQuery: !teamName
 *   }}
 *   preferencesConfig={{
 *     saveWidths: saveColumnWidths,
 *     preferences: tablePreferences,
 *     setPreferences: setTablePreferences,
 *     contentDisplayPreferenceOptions: columnOptions,
 *     selectionType: "multi",
 *     defaultPageSize: 25
 *   }}
 *   propertyFilter={{
 *     filteringProperties: taskFilterProperties,
 *     enableFreeTextSearch: true
 *   }}
 *   autocomplete={{
 *     loadUniqueValues: loadTaskUniqueValues,
 *     fieldToColumnIdMapping: { taskName: 'task-name' }
 *   }}
 *   externalFilterQuery={externalFilter} // Optional: programmatically set filters
 *   headerActions={<Button>Custom Action</Button>} // Optional: custom header actions
 *   onSelectionChange={(items) => console.log(items)} // Optional: selection callback
 * />
 * ```
 */
export interface IServerSideTableWithPropertyFilterProps {
  // Top-level props (most essential and commonly used)
  /**
   * AWS UI column definitions for the table
   * @example [{ id: 'name', header: 'Task Name', cell: item => item.taskName }]
   */
  columnDefinitions: TableProps.ColumnDefinition<any>[];

  /**
   * Title displayed in the table header
   * @example "Tasks", "Audit Items", "User Management"
   */
  tableTitle: string;

  /**
   * Data fetching and loading configuration (REQUIRED)
   */
  dataSource: DataSourceConfig;

  /**
   * Table preferences and user settings (REQUIRED)
   */
  preferencesConfig: TablePreferencesConfig;

  /**
   * PropertyFilter search and filtering configuration (OPTIONAL)
   */
  propertyFilter?: PropertyFilterConfig;

  /**
   * Smart autocomplete and unique values loading (OPTIONAL)
   */
  autocomplete?: AutocompleteConfig;

  /**
   * External filter query to programmatically set filters (OPTIONAL)
   * When provided, this will replace the current filter state.
   * Useful for implementing clickable stat cards or preset filters.
   * @example { tokens: [{ propertyKey: 'status', operator: '!=', value: 'Complete' }], operation: 'and' }
   */
  externalFilterQuery?: PropertyFilterProps.Query;

  /**
   * Callback fired when table selection changes (OPTIONAL)
   * Used when selectionType is "single" or "multi"
   * Useful for parent components to track selected items
   * @example (items) => setSelectedItems(items)
   */
  onSelectionChange?: (items: any[]) => void;

  /**
   * Custom header actions to display in table header (OPTIONAL)
   * Useful for bulk operations on selected items
   * @example <ButtonDropdown items={[...]} />
   */
  headerActions?: React.ReactNode;
  selectedItems?: any[];

  /**
   * Callback when query hook is ready (OPTIONAL)
   * Provides access to query controls like refetch
   * @example (controls) => setRefetchFn(() => controls.refetch)
   */
  onQueryReady?: (queryControls: { refetch: () => void }) => void;

  expandableRows?: TableProps.ExpandableRows<any>;
  isItemDisabled?: (item: any) => boolean;
  trackBy?: string;
}

export const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizePreference["options"] =
  [
    { value: 10, label: "10 items" },
    { value: 25, label: "25 items" },
    { value: 50, label: "50 items" },
    { value: 100, label: "100 items" },
  ];
